import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input, Avatar } from '@heroui/react';
import { Card, CardBody, CardHeader, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useUpdateProfile } from '@/features/profile/hooks';
import { profileSchema, type ProfileValues } from '@/features/profile/schema';

/** View + edit the current user's profile (identity fields; email is immutable). */
export function ProfileSection() {
  const { user } = useAuth();
  const update = useUpdateProfile();
  const form = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { displayName: '', firstName: '', lastName: '' },
  });

  useEffect(() => {
    if (user) form.reset({ displayName: user.displayName, firstName: '', lastName: '' });
  }, [user]); // eslint-disable-line

  const onSubmit = form.handleSubmit(async (v) => { await update.mutateAsync(v); });

  return (
    <Card className="max-w-2xl">
      <CardHeader title="Profile" description="Your display identity across the console." />
      <CardBody>
        <div className="mb-6 flex items-center gap-4">
          <Avatar size="lg" name={(user?.displayName || user?.email || '?').slice(0, 2).toUpperCase()} className="h-16 w-16 bg-brand/15 text-brand" />
          <div>
            <p className="font-medium text-fg">{user?.displayName}</p>
            <p className="text-sm text-fg-muted">{user?.email}</p>
          </div>
        </div>
        <form id="profile-form" onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input isReadOnly label="Email" value={user?.email ?? ''} variant="flat" description="Contact an administrator to change your email." />
          <Input {...form.register('displayName')} isReadOnly label="Display name" variant="bordered"
            isInvalid={!!form.formState.errors.displayName} errorMessage={form.formState.errors.displayName?.message} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input {...form.register('firstName')} isReadOnly label="First name" variant="bordered" />
            <Input {...form.register('lastName')} isReadOnly label="Last name" variant="bordered" />
          </div>
        </form>
      </CardBody>
      <CardFooter>
        {/* No identity endpoint accepts profile edits yet (PATCH /users/me is unrouted). */}
        <p className="mr-auto text-sm text-fg-muted">Profile editing is not enabled yet.</p>
        <Button color="primary" type="submit" form="profile-form" isLoading={update.isPending} isDisabled>Save changes</Button>
      </CardFooter>
    </Card>
  );
}
